/**
 * Async Action Manager
 *
 * Handles submission and polling of async actions.
 * Implements exponential backoff polling strategy.
 */

import { httpClient } from '@/utils/http-client';
import { logger, LogCategory } from '@/utils/logger';
import { loggedFetch, FetchTarget } from '@/utils/logged-fetch';
import {
  AsyncSubmitResponse,
  AsyncPollResponse,
  AsyncRequestStatus,
} from '@/business/types/async.types';

/**
 * Progress callback for status transitions
 */
export type ProgressCallback = (
  status: AsyncRequestStatus,
  metadata?: {
    requestId?: string;
    attempt?: number;
    elapsedMs?: number;
  }
) => void;

/**
 * Polling configuration
 */
const POLLING_CONFIG = {
  INITIAL_DELAY_MS: 100, // Start with 100ms
  MAX_DELAY_MS: 2000, // Cap at 2 seconds
  BACKOFF_MULTIPLIER: 2, // Double each time
  MAX_ATTEMPTS: 60, // ~60 seconds max
  TIMEOUT_MS: 60000, // Hard timeout at 60 seconds
};

/**
 * AsyncActionManager handles submission and polling of async actions
 */
/**
 * Rate limit configuration to prevent runaway loops
 */
const RATE_LIMIT = {
  MAX_PER_WINDOW: 5,
  WINDOW_MS: 5000,
};

export class AsyncActionManager {
  private organizationIdProvider: (() => string | undefined) | null = null;
  private recentSubmissions: Map<string, number[]> = new Map();

  /**
   * Set a provider function that returns the current active organization ID.
   * The returned ID is auto-merged into every action's data payload.
   */
  setOrganizationIdProvider(provider: () => string | undefined): void {
    this.organizationIdProvider = provider;
  }

  /**
   * Get the current active organization ID from the provider.
   * Used by ActionOrchestrator to inject org_id before validation.
   */
  getOrganizationId(): string | undefined {
    return this.organizationIdProvider?.();
  }

  /**
   * Submit an action and wait for completion
   *
   * Flow:
   * 1. POST to /v1/async/{action} → receive requestId
   * 2. Poll GET /v1/async/{action}/{requestId} until terminal state
   * 3. Return poll response when complete
   *
   * @param action - The action type (e.g., 'login')
   * @param data - Request data to send
   * @param onProgress - Optional callback for status transitions
   * @returns Poll response with action result
   */
  async submitAndWait<TData = unknown>(
    action: string,
    data: Record<string, unknown>,
    onProgress?: ProgressCallback
  ): Promise<AsyncPollResponse<TData>> {
    logger.info('AsyncActionManager: Submitting action', { action });

    // Rate limit check — prevents infinite-loop bugs from DDoSing the backend
    this.checkRateLimit(action);

    // Step 1: Submit the action
    const requestId = await this.submitAction(action, data);

    // Call progress callback for SUBMITTED status
    onProgress?.(AsyncRequestStatus.SUBMITTED, { requestId });

    // Step 2: Poll for completion (with progress callbacks)
    const response = await this.pollForCompletion<TData>(
      requestId,
      action,
      onProgress
    );

    return response;
  }

  /**
   * Submit an action to the backend
   *
   * @param action - The action type
   * @param data - Request data
   * @returns Request ID for polling
   */
  private async submitAction(
    action: string,
    data: Record<string, unknown>
  ): Promise<string> {
    try {
      logger.debug('AsyncActionManager: Submitting to endpoint', { action });

      // POST to /api/v1/async/{action}
      // Note: organization_id is injected by ActionOrchestrator before validation,
      // so it's already present in the data object by this point.
      const response = await httpClient.post<AsyncSubmitResponse>(
        `/api/v1/async/${action}`,
        { data }
      );

      // Validate response
      if (
        !response ||
        response.status !== AsyncRequestStatus.SUBMITTED ||
        !response.request_id
      ) {
        logger.error('AsyncActionManager: Invalid submission response', {
          action,
          response,
        });
        throw new Error('Invalid submission response from server');
      }

      logger.info('AsyncActionManager: Action submitted successfully', {
        action,
        requestId: response.request_id,
      });

      return response.request_id;
    } catch (error) {
      logger.error('AsyncActionManager: Submit failed', { action, error });
      throw error;
    }
  }

  /**
   * Poll for action completion with exponential backoff
   *
   * @param requestId - The request ID from submission
   * @param action - The action name (for logging and endpoint)
   * @param onProgress - Optional callback for status transitions
   * @returns Poll response when action reaches terminal state
   */
  async pollForCompletion<TData = unknown>(
    requestId: string,
    action: string,
    onProgress?: ProgressCallback
  ): Promise<AsyncPollResponse<TData>> {
    logger.info('AsyncActionManager: Starting polling', {
      action,
      requestId,
    });

    let attempt = 0;
    let delayMs = POLLING_CONFIG.INITIAL_DELAY_MS;
    const startTime = Date.now();

    while (attempt < POLLING_CONFIG.MAX_ATTEMPTS) {
      // Check hard timeout
      if (Date.now() - startTime > POLLING_CONFIG.TIMEOUT_MS) {
        logger.error('AsyncActionManager: Polling timed out', {
          action,
          requestId,
          attempts: attempt,
          elapsedMs: Date.now() - startTime,
        });

        throw new Error('Polling timeout exceeded');
      }

      attempt++;

      // Wait before polling (skip on first attempt for fast responses)
      if (attempt > 1) {
        await this.sleep(delayMs);

        // Exponential backoff (double each time, capped at MAX_DELAY_MS)
        delayMs = Math.min(
          delayMs * POLLING_CONFIG.BACKOFF_MULTIPLIER,
          POLLING_CONFIG.MAX_DELAY_MS
        );
      }

      try {
        logger.debug('AsyncActionManager: Polling attempt', {
          action,
          requestId,
          attempt,
          delayMs,
        });

        // GET /api/v1/async/{action}/{requestId}
        const response = await httpClient.get<AsyncPollResponse<TData>>(
          `/api/v1/async/${action}/${requestId}`
        );

        // Handle unexpected null response
        if (!response) {
          logger.warn('AsyncActionManager: Null response received, retrying', {
            action,
            requestId,
            attempt,
          });
          continue;
        }

        logger.debug('AsyncActionManager: Poll response', {
          action,
          requestId,
          attempt,
          status: response.status,
          hasData: !!response.data,
        });

        // Call progress callback for status transition
        onProgress?.(response.status, { requestId, attempt });

        // Check if we've reached a terminal state
        if (this.isTerminalStatus(response.status)) {
          logger.info('AsyncActionManager: Terminal state reached', {
            action,
            requestId,
            status: response.status,
            attempts: attempt,
            elapsedMs: Date.now() - startTime,
          });

          // TODO: Add output validation when validators are generated
          // Response validation would help catch backend/frontend type mismatches
          // Currently skipped as output validators are not yet generated

          return response;
        }

        // Status is SUBMITTED or IN_PROGRESS, continue polling
        logger.debug('AsyncActionManager: Non-terminal status, continuing', {
          action,
          requestId,
          status: response.status,
        });
      } catch (error) {
        logger.error('AsyncActionManager: Poll attempt failed', {
          action,
          requestId,
          attempt,
          error,
        });

        // Check if this is a real connectivity issue
        try {
          await loggedFetch(
            'https://www.google.com/generate_204',
            { mode: 'no-cors' },
            {
              category: LogCategory.NETWORK,
              target: FetchTarget.CONNECTIVITY_PROBE,
            }
          );
          // Connectivity probe succeeded — transient server error, keep polling
          logger.warn('AsyncActionManager: Transient error, continuing', {
            action,
            requestId,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        } catch {
          // Connectivity probe also failed — real network issue, bail out
          logger.error(
            'AsyncActionManager: Connectivity check failed, aborting poll',
            {
              action,
              requestId,
              attempt,
            }
          );
          throw new Error('Network connectivity lost during polling');
        }
      }
    }

    // Max attempts reached without terminal state
    logger.error('AsyncActionManager: Max attempts exceeded', {
      action,
      requestId,
      attempts: POLLING_CONFIG.MAX_ATTEMPTS,
      elapsedMs: Date.now() - startTime,
    });

    throw new Error('Max polling attempts exceeded');
  }

  /**
   * Check if status is terminal (COMPLETED or FAILED)
   */
  private isTerminalStatus(status: AsyncRequestStatus): boolean {
    return (
      status === AsyncRequestStatus.COMPLETED ||
      status === AsyncRequestStatus.FAILED
    );
  }

  // TODO: Implement validateActionOutput when output validators are generated
  // This would help catch backend/frontend type mismatches for response data

  /**
   * Rate limiter — prevents the same action from being submitted
   * more than MAX_PER_WINDOW times within WINDOW_MS milliseconds.
   */
  private checkRateLimit(action: string): void {
    const now = Date.now();
    const timestamps = (this.recentSubmissions.get(action) || []).filter(
      t => now - t < RATE_LIMIT.WINDOW_MS
    );

    if (timestamps.length >= RATE_LIMIT.MAX_PER_WINDOW) {
      logger.error('AsyncActionManager: Rate limit exceeded', {
        action,
        submissions: timestamps.length,
        windowMs: RATE_LIMIT.WINDOW_MS,
      });
      throw new Error(
        `Rate limit exceeded for action "${action}": ${timestamps.length} submissions in ${RATE_LIMIT.WINDOW_MS}ms`
      );
    }

    timestamps.push(now);
    this.recentSubmissions.set(action, timestamps);
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Singleton instance of AsyncActionManager
 */
export const asyncActionManager = new AsyncActionManager();
